import { CustomerStatus, Customer } from "~/gql/types";
import {
  CustomerFormData,
  ProjectRequestFolderType,
  ProjectRequestFormData,
} from "./types";
import {
  ProjectRequestCustomerArgsInput,
  ProjectRequestFolderArgsInput,
} from "~/gql/types2";
import { FormState } from "~/components/inline-fields-form/types";
import { Alert } from "~/components/snackbar";

export const extractCustomer = (
  formState: FormState
): Customer | null | undefined => {
  const customer = Object.values(formState.fields).find(
    (x) => x.name === "customer"
  )?.value as Customer | null | undefined;

  return customer;
};

export const customerValidator = (
  isGroup: boolean,
  addAlert: React.Dispatch<Alert>,
  customer: Customer | null | undefined
): boolean => {
  if (!isGroup && customer?.status == CustomerStatus.Rejected) {
    const errorMessage = `${customer.name} is rejected. Please pick a different customer`;
    addAlert({
      isSuccess: false,
      message: errorMessage,
      key: `${Math.random()}`,
    });
    return false;
  }

  return true;
};

export const toCustomerValues = (
  values: CustomerFormData
): ProjectRequestCustomerArgsInput => {
  if (values.requestNewCustomer) {
    return {
      newCustomer: {
        name: values.customerName!,
        phone: values.customerPhone!,
        fax: values.customerFax || "",
        address1: values.customerAddress1!,
        address2: values.customerAddress2 || "",
        zip: !values.isInternational ? values.customerZip || "" : "",
        city: values.customerCity!,
        state: !values.isInternational ? values.customerState || "" : "",
        country: values.isInternational ? values.customerCountry || "" : "",
        notes: values.customerNotes || "",
        adminNotes: values.customerAdminNotes || null,
        attn: values.customerAttn || null,
      },
    };
  }

  if (values.editInPlace) {
    return {
      editRequestedCustomer: {
        createCustomerRequestId: values.customer!.createCustomerRequestId,
        name: values.customerName!,
        phone: values.customerPhone!,
        fax: values.customerFax || "",
        address1: values.customerAddress1!,
        address2: values.customerAddress2 || "",
        zip: !values.isInternational ? values.customerZip || "" : "",
        city: values.customerCity!,
        state: !values.isInternational ? values.customerState || "" : "",
        country: values.isInternational ? values.customerCountry || "" : "",
        notes: values.customerNotes || "",
        adminNotes: values.customerAdminNotes || null,
        attn: values.customerAttn || null,
      },
    };
  }

  const customer = values.customer!;

  if (customer.status == CustomerStatus.Approved) {
    return {
      existingCustomerArgs: { existingNumber: customer.number! },
    };
  }

  if (
    customer.status == CustomerStatus.Pending ||
    customer.status == CustomerStatus.InProjectRequest
  ) {
    return {
      pendingCustomerArgs: {
        pendingRequestId: customer.createCustomerRequestId,
      },
    };
  }

  // Typescript can't fathom the idea of this being handled elsewhere
  throw new Error("Selected customer invalid.");
};

export const toFolderArgsInput = (
  values: ProjectRequestFormData
): ProjectRequestFolderArgsInput | undefined => {
  if (!window.config.featureFlags.projectFolders) {
    return undefined;
  }

  if (
    values.folderType == ProjectRequestFolderType.None ||
    !values.folderType
  ) {
    return undefined;
  }

  if (values.folderType == ProjectRequestFolderType.Single) {
    return {
      singleProjectFolderArgs: {
        driveLetter: values.jobsFolderDriveLetter!,
      },
    };
  }

  if (values.folderType == ProjectRequestFolderType.ExistingGrouped) {
    return {
      existingGroupedFolderArgs: {
        groupedFolderId: values.groupedFolder!.id,
      },
    };
  }

  if (values.folderType == ProjectRequestFolderType.NewGrouped) {
    return {
      newGroupedFolderArgs: {
        name: values.newGroupedFolderName!,
        driveLetter: values.newGroupedFolderDriveLetter!,
      },
    };
  }
};
